// Batch H — Purchase Criteria Settings section.
//
// Mirrors DealCheck's "Purchase Criteria" panel: investor rules
// (1%/2%/50%/70%) + ~19 numeric thresholds across 5 sub-sections.
// Optimistic save on every edit (debounced ~600ms), with a sticky
// Pre-flight Check card showing how many of the user's current deals
// would still pass under the proposed criteria.
//
// Field-id and label registry mirrors backend/criteria_engine.py so
// the surface stays in lock-step with what the engine actually evaluates.

import { useEffect, useMemo, useRef, useState } from "react";
import { Info, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// id, label, unit, direction, helper. Mirrors RULE_LABELS in the engine.
const INVESTOR_RULES = [
  { id: "rule_1_percent",  label: "1% rule",  unit: "%", helper: "Monthly rent ≥ X% of purchase price. The classic 1%-rule rental-cashflow test." },
  { id: "rule_2_percent",  label: "2% rule",  unit: "%", helper: "Stricter rule — monthly rent ≥ X% of purchase price. Common in Cleveland/Detroit-tier markets." },
  { id: "rule_50_percent", label: "50% rule", unit: "%", helper: "Operating expenses ≤ X% of gross rent (excludes mortgage). Quick check before underwriting." },
  { id: "rule_70_percent", label: "70% rule", unit: "%", helper: "(Purchase + repairs) ≤ X% of ARV. Standard flip filter — what's left covers profit + carrying costs." },
];

const THRESHOLD_GROUPS = [
  {
    title: "Purchase",
    items: [
      { id: "purchase_price_max",    label: "Max purchase price",    unit: "$", direction: "max" },
      { id: "total_cash_needed_max", label: "Max total cash needed", unit: "$", direction: "max" },
    ],
  },
  {
    title: "Valuation",
    items: [
      { id: "arv_per_sqft_max",   label: "Max ARV / sqft",   unit: "$", direction: "max" },
    ],
  },
  {
    // Phase 47.3 — Price-per-SqFt / Price-per-SqM range filter.
    // PSF max is the classic "don't overpay" lever; PSF min filters out
    // suspiciously cheap listings (foreclosure / title issues). PSM is
    // the LATAM/EU mirror — same property, dollar-per-square-meter,
    // which is how international buyers in Miami / Madrid / Lisbon
    // negotiate. Skipped silently when a deal has no sqft.
    title: "Price normalisation",
    items: [
      { id: "price_per_sqft_min", label: "Min price / sqft",  unit: "$", direction: "min" },
      { id: "price_per_sqft_max", label: "Max price / sqft",  unit: "$", direction: "max" },
      { id: "price_per_sqm_min",  label: "Min price / sqm",   unit: "$", direction: "min" },
      { id: "price_per_sqm_max",  label: "Max price / sqm",   unit: "$", direction: "max" },
    ],
  },
  {
    title: "Cash flow",
    items: [
      { id: "cash_flow_min",          label: "Min monthly cash flow",  unit: "$", direction: "min" },
      { id: "cash_flow_per_unit_min", label: "Min cash flow per unit", unit: "$", direction: "min" },
    ],
  },
  {
    title: "Investment returns",
    items: [
      { id: "cap_rate_min", label: "Min cap rate",          unit: "%", direction: "min" },
      { id: "coc_min",      label: "Min cash-on-cash",      unit: "%", direction: "min" },
      { id: "roe_min",      label: "Min return on equity",  unit: "%", direction: "min" },
      { id: "roi_min",      label: "Min return on investment", unit: "%", direction: "min" },
      { id: "irr_min",      label: "Min IRR",               unit: "%", direction: "min" },
    ],
  },
  {
    title: "Financial ratios",
    items: [
      { id: "rtv_min",              label: "Min rent-to-value",       unit: "%", direction: "min" },
      { id: "grm_max",              label: "Max gross rent multiplier", unit: "x", direction: "max" },
      { id: "equity_multiple_min",  label: "Min equity multiple",      unit: "x", direction: "min" },
      { id: "break_even_ratio_max", label: "Max break-even ratio",     unit: "%", direction: "max" },
      { id: "ltv_max",              label: "Max loan-to-value",        unit: "%", direction: "max" },
      { id: "ltc_max",              label: "Max loan-to-cost",         unit: "%", direction: "max" },
      { id: "dcr_min",              label: "Min debt coverage ratio",  unit: "x", direction: "min" },
      { id: "debt_yield_min",       label: "Min debt yield",           unit: "%", direction: "min" },
    ],
  },
];

function unitPrefix(unit) {
  return unit === "$" ? "$" : "";
}
function unitSuffix(unit) {
  return unit === "%" ? "%" : unit === "x" ? "x" : "";
}

function DirectionTag({ direction }) {
  return (
    <span
      data-testid="criteria-direction"
      className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
        direction === "max"
          ? "bg-rose-50 text-rose-700"
          : "bg-emerald-50 text-emerald-700"
      }`}
    >
      {direction === "max" ? "≤ max" : "≥ min"}
    </span>
  );
}

function CriteriaRow({ rowKey, label, helper, unit, direction, enabled, value,
                       onToggle, onValueChange, threshold = false }) {
  const [localVal, setLocalVal] = useState(value ?? "");
  // Sync down when the parent fetches new data (e.g. after PATCH).
  useEffect(() => {
    setLocalVal(value ?? "");
  }, [value]);

  return (
    <div
      data-testid={`criteria-row-${rowKey}`}
      className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-1 py-3 last:border-b-0"
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <Switch
          data-testid={`criteria-toggle-${rowKey}`}
          checked={!!enabled}
          onCheckedChange={(v) => onToggle(v)}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-medium text-slate-900">{label}</span>
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={`What is ${label}?`}
                    className="rounded-full p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  >
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs">
                  {helper}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {threshold ? <DirectionTag direction={direction} /> : null}
        <div className="flex items-center gap-1">
          {unitPrefix(unit) ? (
            <span className="text-xs text-slate-400">{unitPrefix(unit)}</span>
          ) : null}
          <input
            type="number"
            value={localVal}
            disabled={!enabled}
            onChange={(e) => setLocalVal(e.target.value)}
            onBlur={() => {
              const num = localVal === "" ? null : Number(localVal);
              if (num !== value) onValueChange(num);
            }}
            data-testid={`criteria-input-${rowKey}`}
            className="w-24 rounded-md border border-slate-200 bg-white px-2 py-1 text-right text-sm tabular-nums shadow-sm transition focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
            placeholder={threshold ? "—" : ""}
          />
          {unitSuffix(unit) ? (
            <span className="text-xs text-slate-400">{unitSuffix(unit)}</span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function PurchaseCriteriaSection() {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Debounce timer for the pre-flight refresh.
  const previewTimer = useRef(null);

  // Load existing doc once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/criteria");
        if (!cancelled) setDoc(r.data);
      } catch (err) {
        console.warn("criteria load failed", err);
        toast.error("Could not load purchase criteria.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Run preview whenever the user's criteria changes (debounced).
  const runPreview = useMemo(
    () => async (proposed) => {
      setPreviewLoading(true);
      try {
        const r = await api.post("/criteria/preview", proposed);
        setPreview(r.data);
      } catch (err) {
        console.warn("preview failed", err);
      } finally {
        setPreviewLoading(false);
      }
    },
    [],
  );

  // First load → run initial preview against the saved criteria.
  useEffect(() => {
    if (doc && !preview) {
      runPreview({ rules: doc.rules, thresholds: doc.thresholds });
    }
  }, [doc, preview, runPreview]);

  // Persist a partial patch + optimistic state merge.
  const persist = async (patch, nextDoc) => {
    setSaving(true);
    setDoc(nextDoc);  // optimistic
    try {
      const r = await api.patch("/criteria", patch);
      setDoc(r.data);
      toast.success("Saved");
      // Debounce the preview to avoid hammering the endpoint per keystroke.
      clearTimeout(previewTimer.current);
      previewTimer.current = setTimeout(
        () => runPreview({ rules: r.data.rules, thresholds: r.data.thresholds }),
        600,
      );
    } catch (err) {
      console.error("save failed", err);
      toast.error("Could not save criteria.");
    } finally {
      setSaving(false);
    }
  };

  const setRule = (id, field, value) => {
    if (!doc) return;
    const next = {
      ...doc,
      rules: {
        ...doc.rules,
        [id]: { ...doc.rules[id], [field]: value },
      },
    };
    persist({ rules: { [id]: { [field]: value } } }, next);
  };

  const setThreshold = (id, field, value) => {
    if (!doc) return;
    const next = {
      ...doc,
      thresholds: {
        ...doc.thresholds,
        [id]: { ...doc.thresholds[id], [field]: value },
      },
    };
    persist({ thresholds: { [id]: { [field]: value } } }, next);
  };

  if (loading) {
    return (
      <div className="space-y-3" data-testid="criteria-section-loading">
        <Skeleton className="h-32 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }
  if (!doc) return null;

  return (
    <section
      data-testid="purchase-criteria-section"
      className="space-y-6"
    >
      {/* Pre-flight card */}
      <div
        data-testid="criteria-preflight"
        className="rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50/40 to-white p-5 shadow-sm"
      >
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-indigo-100 p-2 text-indigo-700">
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-indigo-700">
              Pre-flight check
            </h3>
            {previewLoading ? (
              <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-slate-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Re-checking your portfolio…
              </p>
            ) : preview && preview.total_deals > 0 ? (
              <>
                <p className="mt-1 text-base text-slate-900">
                  <span className="font-semibold tabular-nums" data-testid="criteria-preflight-fraction">
                    {preview.passing_deals} of {preview.total_deals}
                  </span>{" "}
                  current deals would pass these criteria
                  {preview.enabled_rule_count > 0 ? (
                    <> ({preview.enabled_rule_count} {preview.enabled_rule_count === 1 ? "rule" : "rules"} enabled)</>
                  ) : (
                    <> — but no rules are enabled yet</>
                  )}
                  .
                </p>
                {preview.top_failures?.length > 0 ? (
                  <p className="mt-1 text-sm text-slate-600">
                    Most common failure:{" "}
                    <span className="font-medium text-slate-900">
                      {preview.top_failures[0].label}
                    </span>{" "}
                    ({preview.top_failures[0].fail_count} {preview.top_failures[0].fail_count === 1 ? "deal" : "deals"})
                    {preview.top_failures[1] ? (
                      <>
                        , then{" "}
                        <span className="font-medium">
                          {preview.top_failures[1].label}
                        </span>{" "}
                        ({preview.top_failures[1].fail_count})
                      </>
                    ) : null}
                    .
                  </p>
                ) : null}
              </>
            ) : (
              <p className="mt-1 text-sm text-slate-500">
                No deals yet — analyse a deal first to see your pre-flight pass rate.
              </p>
            )}
          </div>
          {saving ? (
            <span className="inline-flex items-center gap-1 text-xs text-slate-500">
              <Loader2 className="h-3 w-3 animate-spin" /> Saving
            </span>
          ) : null}
        </div>
      </div>

      {/* Investor rules */}
      <div
        data-testid="criteria-investor-rules"
        className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
      >
        <h3 className="mb-1 text-sm font-semibold uppercase tracking-wider text-slate-500">
          Investor rules
        </h3>
        <p className="mb-4 text-xs text-slate-500">
          The four heuristics most landlords use as gut-checks before they
          even open a spreadsheet.
        </p>
        <div className="divide-y divide-slate-100">
          {INVESTOR_RULES.map((r) => {
            const cfg = doc.rules?.[r.id] || {};
            return (
              <CriteriaRow
                key={r.id}
                rowKey={r.id}
                label={r.label}
                helper={r.helper}
                unit={r.unit}
                direction={r.id === "rule_50_percent" || r.id === "rule_70_percent" ? "max" : "min"}
                enabled={!!cfg.enabled}
                value={cfg.threshold ?? ""}
                onToggle={(v) => setRule(r.id, "enabled", v)}
                onValueChange={(v) =>
                  setRule(r.id, "threshold", v === null ? 0 : Number(v))
                }
                threshold
              />
            );
          })}
        </div>
      </div>

      {/* Threshold groups */}
      {THRESHOLD_GROUPS.map((g) => (
        <div
          key={g.title}
          data-testid={`criteria-group-${g.title.toLowerCase().replace(/\s/g, "-")}`}
          className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
        >
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-500">
            {g.title}
          </h3>
          <div className="divide-y divide-slate-100">
            {g.items.map((item) => {
              const cfg = doc.thresholds?.[item.id] || {};
              return (
                <CriteriaRow
                  key={item.id}
                  rowKey={item.id}
                  label={item.label}
                  helper={`Direction: ${item.direction === "max" ? "no more than" : "at least"} the value you set.`}
                  unit={item.unit}
                  direction={item.direction}
                  enabled={!!cfg.enabled}
                  value={cfg.value ?? ""}
                  onToggle={(v) => setThreshold(item.id, "enabled", v)}
                  onValueChange={(v) => setThreshold(item.id, "value", v)}
                  threshold
                />
              );
            })}
          </div>
        </div>
      ))}

      <p className="px-2 text-xs text-slate-400">
        Changes auto-save. Visit any deal to see the per-rule pass/fail breakdown.
      </p>
    </section>
  );
}
